import { useState } from 'react';
import { AxiosError } from 'axios';
import {
  Box,
  Alert,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  CircularProgress,
  LinearProgress,
  useTheme,
  Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Send as SendIcon, Description as DescriptionIcon } from '@mui/icons-material';
import {
  useGenerateCoInvestorContract,
  useGetCoInvestorContractUrl,
  useGetCoInvestors,
  useResendCoInvestorInvitation,
  useUpdateCoInvestorPaidStatus,
} from '@/lib/api/investments';
import { EditCoInvestorsDialog } from '@/features/investments/co-investment/EditCoInvestorsDialog';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency, formatPercentage } from '@/utils/misc';
import { InvestmentDTO, CoInvestorDTO } from '@inversor-hub/types';
import useSnackbar from '@/hooks/useSnackbar';

interface CoInvestorsTabProps {
  investment: InvestmentDTO;
}

const feeLabel = (ci: CoInvestorDTO): string => {
  if (ci.feeType === 'FIXED' && ci.feeAmount != null) {
    return formatCurrency(ci.feeAmount);
  }
  if (ci.feeType === 'PERCENTAGE' && ci.feePercentage != null) {
    return formatPercentage(ci.feePercentage);
  }
  return '-';
};

const getContractChipConfig = (status: CoInvestorDTO['contractStatus']) => {
  if (status === 'SIGNED') {
    return { label: 'Firmado', color: 'success' as const, variant: 'filled' as const };
  }

  if (status === 'PENDING_SIGNATURE') {
    return {
      label: 'Pendiente firma',
      color: 'warning' as const,
      variant: 'outlined' as const,
    };
  }

  return { label: 'Sin generar', color: 'default' as const, variant: 'outlined' as const };
};

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof AxiosError)) {
    return undefined;
  }

  const responseData = error.response?.data as { error?: string } | undefined;
  return responseData?.error;
};

export const CoInvestorsTab = ({ investment }: CoInvestorsTabProps) => {
  const theme = useTheme();
  const { user } = useAuth();
  const isPsi = user?.role === 'psi';
  const { data: coInvestors, isLoading, isError, refetch } = useGetCoInvestors(investment.id);
  const resendInvitation = useResendCoInvestorInvitation();
  const updatePaidStatus = useUpdateCoInvestorPaidStatus();
  const generateContract = useGenerateCoInvestorContract();
  const getContractUrl = useGetCoInvestorContractUrl();
  const { show: showSnackbar } = useSnackbar();
  const [editing, setEditing] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [updatingPaidId, setUpdatingPaidId] = useState<string | null>(null);
  const [generatingContractId, setGeneratingContractId] = useState<string | null>(null);
  const [openingContractId, setOpeningContractId] = useState<string | null>(null);

  const handleResend = async (coInvestorId: string) => {
    setResendingId(coInvestorId);
    try {
      await resendInvitation.mutateAsync({
        investmentId: investment.id,
        coInvestorId,
      });
      showSnackbar({ title: 'Invitación reenviada', variant: 'success' });
    } catch {
      showSnackbar({ title: 'Error al reenviar la invitación', variant: 'error' });
    } finally {
      setResendingId(null);
    }
  };

  const handlePaidStatusChange = async (coInvestorId: string, isPaid: boolean) => {
    if (!isPsi) {
      return;
    }

    setUpdatingPaidId(coInvestorId);
    try {
      await updatePaidStatus.mutateAsync({
        investmentId: investment.id,
        coInvestorId,
        data: { isPaid },
      });
      showSnackbar({
        title: isPaid ? 'Pago marcado como recibido' : 'Pago marcado como pendiente',
        variant: 'success',
      });
    } catch {
      showSnackbar({ title: 'Error al actualizar el estado de pago', variant: 'error' });
    } finally {
      setUpdatingPaidId(null);
    }
  };

  const handleGenerateContract = async (coInvestorId: string) => {
    setGeneratingContractId(coInvestorId);
    try {
      const { url } = await generateContract.mutateAsync({
        investmentId: investment.id,
        coInvestorId,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
      showSnackbar({ title: 'Contrato generado correctamente', variant: 'success' });
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error);

      showSnackbar({
        title: 'Error al generar el contrato',
        message: errorMessage,
        variant: 'error',
      });
    } finally {
      setGeneratingContractId(null);
    }
  };

  const handleOpenContract = async (coInvestorId: string) => {
    setOpeningContractId(coInvestorId);
    try {
      const { url } = await getContractUrl.mutateAsync({
        investmentId: investment.id,
        coInvestorId,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      showSnackbar({ title: 'Error al abrir el contrato', variant: 'error' });
    } finally {
      setOpeningContractId(null);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" action={<Button onClick={() => refetch()}>Reintentar</Button>}>
        No se han podido cargar los coinversores.
      </Alert>
    );
  }

  if (!coInvestors || coInvestors.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography color="text.secondary">No hay coinversores registrados.</Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {isPsi && (
        <Stack spacing={1} alignItems="flex-start">
          <Button
            variant="outlined"
            onClick={() => setEditing(true)}
            disabled={
              coInvestors.some((ci) => ci.contractStatus === 'SIGNED') ||
              investment.capex.acquisitionPrice <= 0 ||
              !!updatingPaidId ||
              !!generatingContractId
            }
          >
            Editar coinversores
          </Button>
          {coInvestors.some((ci) => ci.contractStatus === 'SIGNED') && (
            <Typography variant="body2" color="text.secondary">
              El reparto no se puede editar porque hay contratos firmados.
            </Typography>
          )}
          {investment.capex.acquisitionPrice <= 0 && (
            <Typography variant="body2" color="text.secondary">
              Indica primero un precio de adquisición mayor que cero.
            </Typography>
          )}
        </Stack>
      )}
      {editing && (
        <EditCoInvestorsDialog
          investment={investment}
          coInvestors={coInvestors}
          onClose={() => setEditing(false)}
        />
      )}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Distribución de propiedad
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1.5}>
          {coInvestors.map((ci) => (
            <Box key={ci.id}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {ci.investorName || ci.email}
                </Typography>
                <Chip
                  label={formatPercentage(ci.ownershipPercentage)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: alpha(theme.palette.text.primary, 0.06),
                    color: 'text.primary',
                  }}
                />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={ci.ownershipPercentage * 100}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  backgroundColor: alpha(theme.palette.text.primary, 0.12),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            </Box>
          ))}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Coinversores
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-head': {
                color: 'text.secondary',
                fontWeight: 600,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Inversor</TableCell>
                <TableCell>Participación</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Contrato</TableCell>
                <TableCell>Pago</TableCell>
                {isPsi && <TableCell>Honorarios</TableCell>}
                {isPsi && <TableCell sx={{ width: 160 }}>Acciones</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {coInvestors.map((ci) => {
                const contractChipConfig = getContractChipConfig(ci.contractStatus);

                return (
                  <TableRow
                    key={ci.id}
                    hover
                    sx={{
                      '&:last-child td, &:last-child th': { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Avatar
                          src={ci.investorPhotoUrl}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: alpha(theme.palette.primary.main, 0.16),
                            color: 'primary.dark',
                          }}
                        >
                          {(ci.investorName || ci.email).charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {ci.investorName || ci.email}
                          </Typography>
                          {ci.investorName && (
                            <Typography variant="caption" color="text.secondary">
                              {ci.email}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {formatPercentage(ci.ownershipPercentage)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ci.invitationStatus === 'ACCEPTED' ? 'Aceptada' : 'Pendiente'}
                        size="small"
                        color={ci.invitationStatus === 'ACCEPTED' ? 'success' : 'warning'}
                        variant={ci.invitationStatus === 'ACCEPTED' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={contractChipConfig.label}
                        size="small"
                        color={contractChipConfig.color}
                        variant={contractChipConfig.variant}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip
                          label={ci.isPaid ? 'Pagado' : 'Pendiente'}
                          size="small"
                          color={ci.isPaid ? 'success' : 'default'}
                          variant={ci.isPaid ? 'filled' : 'outlined'}
                        />
                        {isPsi && (
                          <Switch
                            size="small"
                            checked={ci.isPaid}
                            onChange={(e) => handlePaidStatusChange(ci.id, e.target.checked)}
                            disabled={updatingPaidId === ci.id}
                          />
                        )}
                        {updatingPaidId === ci.id && <CircularProgress size={14} />}
                      </Stack>
                    </TableCell>
                    {isPsi && (
                      <TableCell>
                        <Typography variant="body2">{feeLabel(ci)}</Typography>
                      </TableCell>
                    )}
                    {isPsi && (
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {ci.invitationStatus === 'PENDING' && (
                            <Tooltip title="Reenviar invitación">
                              <IconButton
                                size="small"
                                onClick={() => handleResend(ci.id)}
                                disabled={resendingId === ci.id}
                                color="primary"
                              >
                                {resendingId === ci.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <SendIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}

                          {ci.contractStatus === 'NOT_GENERATED' && (
                            <Tooltip
                              title={
                                ci.invitationStatus !== 'ACCEPTED'
                                  ? 'El coinversor debe aceptar la invitación antes de generar el contrato'
                                  : 'Generar contrato'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleGenerateContract(ci.id)}
                                  disabled={
                                    ci.invitationStatus !== 'ACCEPTED' ||
                                    generatingContractId === ci.id
                                  }
                                >
                                  {generatingContractId === ci.id ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <DescriptionIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {ci.contractStatus !== 'NOT_GENERATED' && (
                            <Tooltip title="Ver/Descargar contrato">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenContract(ci.id)}
                                disabled={openingContractId === ci.id}
                              >
                                {openingContractId === ci.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <DescriptionIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
};
